import { usePlaybookCategories } from '@/shared/hooks/data-hooks/playbooks/usePlaybookCategories'
import { usePlaybooksInfinite } from '@/shared/hooks/data-hooks/playbooks/usePlaybooksInfinite'
import { useFeatureFlagsWithWorkspace } from '@/shared/hooks/useFeatureFlags'
import { useInfiniteScroll } from '@/shared/hooks/useInfiniteScroll'
import { keepPreviousData } from '@tanstack/react-query'
import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CategoryTags } from './CategoryTags'
import { ITEMS_PER_PAGE } from './constants'
import { EmptyState } from './EmptyState'
import { ErrorState } from './ErrorState'
import { InfiniteScrollTrigger } from './InfiniteScrollTrigger'
import { PlaybookCardsGrid } from './PlaybookCardsGrid'
import { PlaybookCardSkeleton } from './PlaybookCardSkeleton'
import { PlaybookCardsSectionProps } from './types'

export const PlaybookCardsSection: React.FC<PlaybookCardsSectionProps> = ({
  filters,
  maxPlaybooks,
  disableInfiniteScroll,
  cardVariant = 'default',
  hideCategoryTags,
  onPlaybookAction,
}) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { flags: [isPlaybookCategorizationEnabled] } = useFeatureFlagsWithWorkspace([
    'playbookCategorization',
  ])

  const {
    data: { data: categories = [] } = {},
    isLoading: isLoadingCategories,
  } = usePlaybookCategories({
    queryOptions: {
      enabled: !hideCategoryTags,
      placeholderData: keepPreviousData,
    },
  })

  const selectedCategoryId = searchParams.get('category')
  const selectedCategory = categories?.find(
    (category) => category._id === selectedCategoryId
  )

  const queryFilters = {
    ...filters,
    ...(searchParams.get('category')
      ? { category: searchParams.get('category') ?? undefined }
      : {}),
  }

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePlaybooksInfinite({
    filters: queryFilters,
    limit: maxPlaybooks || ITEMS_PER_PAGE,
  })

  const loadMoreRef = useInfiniteScroll({
    onLoadMore: fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    disable: disableInfiniteScroll,
  })

  const handleNavigateToDashboard = () => navigate('/dashboard?new=true')
  const handleRetry = () => window.location.reload()

  const allPlaybooks = data?.pages?.flatMap((page) => page.data) || []
  const hasPlaybooks = (data?.pages?.[0]?.metadata.total ?? 0) > 0

  return (
    <section className="mx-auto w-full">
      {isPlaybookCategorizationEnabled && !hideCategoryTags && (
        <CategoryTags
          isLoading={isLoadingCategories}
          categories={(categories ?? []).filter((category) =>
            Number.isFinite(category.playbookCount)
              ? category.playbookCount > 0
              : false
          )}
          selectedCategory={selectedCategory}
        />
      )}

      <div>
        {isLoading && <PlaybookCardSkeleton />}

        {error && <ErrorState error={error} onRetry={handleRetry} />}

        {hasPlaybooks && (
          <>
            <PlaybookCardsGrid
              playbooks={allPlaybooks}
              cardVariant={cardVariant}
              onPlaybookAction={onPlaybookAction}
            />

            <InfiniteScrollTrigger
              loadMoreRef={loadMoreRef}
              isFetchingNextPage={isFetchingNextPage}
            />
          </>
        )}

        {!isLoading && !error && !hasPlaybooks && (
          <EmptyState onNavigateToDashboard={handleNavigateToDashboard} />
        )}
      </div>
    </section>
  )
}
